import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map } from 'immutable'
import { getBoundActions } from '../../actions/index'
import SpotlightLiveAccessV2 from '../SpotlightLiveAccessV2/index'
import SectionGraphQLManager, { SECTION_TYPES } from '../SectionGraphQLManager/index'
import {
  PM_PLACEMENT_PAYLOAD_TYPE_SPOTLIGHT_LIVE_ACCESS,
  PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT,
  PM_PLACEMENT_PAYLOAD_TYPE_FEATURED_CONTENT_IMPRESSION,
} from '../../services/page-manager/constants'
import { selectSpotlightSectionById } from '../../services/page-manager/index'
import { getUserLanguagePrimary } from '../../services/user/index'
import { EN } from '../../services/languages/constants'
import {
  SPOTLIGHT_IMPRESSION_GA4_EVENT,
} from '../../services/event-tracking/index'
import { MH_SPOTLIGHT_COLLAPSED_KEY } from '../../services/feature-tracking/index'
import { createTitleGroupId } from '../../services/content/index'
import { getPmSectionDataCreated, preventSectionDataLoad } from '../../services/pm-section/index'
import SpotlightWinbackBannerV2 from '../SpotlightWinbackBannerV2/index'
import OptimizelyFeature from '../OptimizelyFeature/index'
import { WINBACK_FLAG, WINBACK_VARIATION, shouldShowWinbackCheckout } from '../../services/winback/index'

const SPOTLIGHT_A = 'member-home-spotlight-a-v1'

/**
 * The page manager section placement component
 */
class SectionPlacement extends Component {
  constructor (props) {
    super(props)
    this.gaEventSent = false
  }

  componentDidMount () {
    const {
      auth,
      user,
      userAccount,
      paytrack,
      updateOptimizelyFeatureUser,
    } = this.props

    // Winback Flow: Update the user_is_winback attribute for the winback flow
    const isWinback = shouldShowWinbackCheckout(auth, user, userAccount, paytrack)
    updateOptimizelyFeatureUser({
      user_is_winback: isWinback,
    })
  }

  onVisibilityChange = (isVisible, contentData) => {
    const { props } = this
    const {
      setEventDefault,
      ga4ImpressionEventData,
      sectionId,
      pmSection,
      language,
    } = props
    const now = new Date()
    const dataCreated = getPmSectionDataCreated({ pmSection, language, sectionId })
    const preventDataLoad = preventSectionDataLoad({ now, dataCreated })

    // if data is loading or needing to be loaded, bail
    if (!preventDataLoad) {
      return
    }

    // Only send GA data once
    if (isVisible && !this.gaEventSent && contentData) {
      const seriesId = contentData.get('seriesId')
      // GA4 event here
      setEventDefault(ga4ImpressionEventData.mergeDeep({
        content_name: contentData.get('title'),
        content_id: contentData.get('id'),
        content_type: contentData.get('contentType'),
        parent_series_id: seriesId > 0 ? seriesId : undefined,
        parent_series_name: contentData.get('seriesTitle') || undefined,
      }))
      this.gaEventSent = true
    }
  }

  render () {
    const { props } = this
    const {
      placementName,
      payloadType,
      pmPlacementData,
      sectionId,
      history,
      sectionIndex,
      ga4ImpressionEventData,
      setEventDefault,
      language,
    } = props

    const renderSpotlight = () => {
      if (payloadType === PM_PLACEMENT_PAYLOAD_TYPE_SPOTLIGHT_LIVE_ACCESS) {
        return (
          <SpotlightLiveAccessV2
            data={pmPlacementData || Map()}
            onVisibilityChange={() => setEventDefault(ga4ImpressionEventData)}
          />
        )
      }

      const titleGroupIds = [createTitleGroupId(pmPlacementData.get('contentId'), pmPlacementData.get('contentType'))]
      return (
        <SectionGraphQLManager
          language={language}
          titleGroupIds={titleGroupIds}
          sectionId={sectionId}
          history={history}
          sectionIndex={sectionIndex}
          onVisibilityChange={this.onVisibilityChange}
          ga4ImpressionEventData={ga4ImpressionEventData || Map()}
          sectionType={SECTION_TYPES.SPOTLIGHT}
        />
      )
    }

    switch (placementName) {
      case SPOTLIGHT_A: {
        return (
          <>
            <OptimizelyFeature
              flag={WINBACK_FLAG}
              variation="control"
              isDefault={true}
              anon={false}
              noWrapper
            >
              {renderSpotlight()}
            </OptimizelyFeature>
            <OptimizelyFeature
              flag={WINBACK_FLAG}
              variation={WINBACK_VARIATION}
              noWrapper
            >
              <SpotlightWinbackBannerV2 history={history} />
            </OptimizelyFeature>
          </>
        )
      }
      default:
        return null
    }
  }
}

SectionPlacement.propTypes = {
  setEventDefault: PropTypes.func.isRequired,
  auth: PropTypes.object,
  user: PropTypes.object,
  userAccount: PropTypes.object,
  updateOptimizelyFeatureUser: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        app,
        page,
        auth,
        user,
        userAccount,
        paytrack,
        pmSection,
      } = state
      const { sectionId } = props
      const language = getUserLanguagePrimary(user) || EN
      // Get section
      const placementName = pmSection.getIn([sectionId, 'data', 'data', 'placement'])

      const {
        hasSectionData,
        pmPlacementData,
        payloadType,
        contentType,
        contentId,
        hasPlacementData,
        campaignId,
        campaignName,
        merchEventId,
        source,
        score,
        processing: placementProcessing,
        viewMode,
        slug,
      } = selectSpotlightSectionById(state, sectionId)
      const eventAMA = pmPlacementData.get('eventType') === 'AMA'

      const getGa4ImpressionEventData = () => {
        const eventData = SPOTLIGHT_IMPRESSION_GA4_EVENT.mergeDeep({
          row_number: 0,
          row_placement: 0,
          row_type: SPOTLIGHT_A,
          row_name: campaignName,
          ...(slug && { spotlight_campaign: slug }),
        })

        switch (payloadType) {
          case PM_PLACEMENT_PAYLOAD_TYPE_FEATURED_CONTENT_IMPRESSION:
            return eventData.mergeDeep({
              has_trailer: false,
              spotlight_type: 'content impression',
            })
          case PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT:
            return eventData.mergeDeep({
              has_trailer: viewMode === 'trailer',
              spotlight_type: 'merch campaign',
            })
          case PM_PLACEMENT_PAYLOAD_TYPE_SPOTLIGHT_LIVE_ACCESS:
            return eventData.mergeDeep({
              has_trailer: false,
              spotlight_type: eventAMA ? 'AMA' : 'event',
            })
          default:
            return eventData.mergeDeep({
              has_trailer: false,
              spotlight_type: 'recommendation',
            })
        }
      }

      return {
        app,
        page,
        auth,
        user,
        userAccount,
        paytrack,
        hasSectionData,
        hasPlacementData,
        campaignId,
        payloadType,
        pmPlacementData: pmPlacementData || Map(),
        placementName,
        placementProcessing,
        language,
        contentType,
        contentId,
        merchEventId,
        pmSection,
        source,
        score,
        ga4ImpressionEventData: getGa4ImpressionEventData(),
        spotlightMinimized: state.featureTracking.getIn(['data', MH_SPOTLIGHT_COLLAPSED_KEY]),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
        updateOptimizelyFeatureUser: actions.optimizelyFeature.updateOptimizelyFeatureUser,
      }
    },
  ),
)(SectionPlacement)
